import { CharacterArchetype, LayerType, CharacterStylePreset } from '../types';

export interface ArchetypePrompts {
  body: string;
  head: string; // Combined face + hair for unified head layer
  face: string;
  hair: string;
  clothing: string;
  accessories: string;
}

export const CHARACTER_ARCHETYPES = {
  film: {
    label: 'Film/Photorealistic Character',
    description: 'Cinema quality, hyper-realistic character with detailed textures and professional lighting',
    color: 'from-blue-500 to-cyan-500',
    borderColor: 'blue',
    badge: 'Photorealistic',
    stylePreset: 'photorealistic' as CharacterStylePreset,
  },
  game_3d: {
    label: '3D/Animation Character',
    description: 'Game-ready 3D character design with optimized geometry and stylized realism',
    color: 'from-green-500 to-emerald-500',
    borderColor: 'green',
    badge: '3D/Animation',
    stylePreset: 'photorealistic' as CharacterStylePreset, // 3D falls under photorealistic for now
  },
  manga_anime: {
    label: 'Manga/Anime Character',
    description: 'Japanese animation style with expressive features and beautiful illustrations',
    color: 'from-purple-500 to-pink-500',
    borderColor: 'purple',
    badge: 'Anime/Manga',
    stylePreset: 'anime' as CharacterStylePreset,
  },
  comic_cartoon: {
    label: 'Comic/Cartoon Character',
    description: 'Western comics/cartoons with bold lines, dynamic poses, and vibrant colors',
    color: 'from-orange-500 to-red-500',
    borderColor: 'orange',
    badge: 'Comic/Cartoon',
    stylePreset: 'comic' as CharacterStylePreset,
  },
};

/**
 * Curated prompts for each archetype and layer type.
 * These prompts are appended to user descriptions to ensure consistent styling.
 */
export const ARCHETYPE_LAYER_PROMPTS: Record<CharacterArchetype, ArchetypePrompts> = {
  film: {
    body: 'photorealistic, realistic anatomy, detailed skin texture, professional lighting, cinematic quality, high fashion photography, studio lighting',
    head: 'photorealistic portrait photography, professional beauty headshot, soft diffused key light with fill light, butterfly lighting setup, catchlights in eyes, natural skin texture with soft luminous glow, detailed pores and skin texture, professional retouching aesthetic, shallow depth of field, bokeh background blur, detailed hair texture with individual strands visible, salon quality hair styling, soft wraparound lighting, high-end editorial portrait, beauty photography lighting, flawless yet natural skin, soft specular highlights, cinema quality rendering',
    face: 'photorealistic, detailed facial features, professional makeup, realistic skin tones, high resolution, cinema lighting, beautiful proportions',
    hair: 'photorealistic, detailed hair texture, realistic hair strands, professional salon quality, volumetric lighting, cinematic rendering',
    clothing: 'photorealistic, realistic fabric textures, professional tailoring, high-end fashion, detailed stitching, cinema quality materials',
    accessories: 'photorealistic, detailed metalwork, realistic materials, professional polish, cinematic lighting, high-end craftsmanship',
  },
  game_3d: {
    body: '3D game character, game-ready topology, stylized realism, detailed surface normal maps, PBR textures, optimized geometry, video game quality',
    head: '3D game character, stylized realism, expressive eyes, game-ready mesh, detailed skin normal maps, video game lighting, gaming quality, game-ready hair geometry, stylized hair, realistic flow, video game textures',
    face: '3D game character, stylized realism, expressive eyes, game-ready mesh, detailed skin normal maps, video game lighting, gaming quality',
    hair: '3D game character, game-ready geometry, stylized hair, realistic flow, video game textures, optimized polygon count',
    clothing: '3D game character, game-ready clothing, realistic fabric fold, PBR textures, video game quality, optimized mesh',
    accessories: '3D game character, game-ready objects, stylized realism, PBR textures, video game quality, optimized geometry',
  },
  manga_anime: {
    body: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, illustrated drawn character, high quality anime manga artwork, anime art, manga art',
    head: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, anime face, anime eyes, anime features, illustrated drawn character, high quality anime manga artwork, anime art, manga art',
    face: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, anime face, anime eyes, anime features, illustrated drawn artwork',
    hair: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, anime hair, illustrated drawn artwork',
    clothing: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, illustrated clothing, anime manga artwork',
    accessories: 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration, Japanese animation character design, illustrated accessories, anime manga artwork',
  },
  comic_cartoon: {
    body: 'modern comic book style, western graphic novel, sharp ink lines, digital coloring, dynamic shading, slight crosshatching, vibrant colors, clean finish, high quality illustration',
    head: 'comic book style, comic book face, expressive features, sharp ink lines, digital coloring, vibrant colors, dramatic expressions, western comic book illustration, comic book hair, western comic style, dramatic volume, stylized texture, high quality illustration',
    face: 'comic book face, expressive features, sharp ink lines, digital coloring, vibrant colors, dramatic expressions, western comic book illustration',
    hair: 'comic book hair, western comic style, sharp ink lines, vibrant colors, dramatic volume, stylized texture, high quality illustration',
    clothing: 'comic book clothing, sharp ink lines, western graphic novel style, vibrant colors, dynamic folds, western illustration quality, digital coloring',
    accessories: 'comic book accessories, western comic style, sharp ink lines, vibrant colors, dramatic design, comic book illustration',
  },
};

/**
 * Get the prompt suffix for a specific archetype and layer type
 */
export function getArchetypeLayerPrompt(archetype: CharacterArchetype, layerType: LayerType): string {
  return ARCHETYPE_LAYER_PROMPTS[archetype][layerType as keyof ArchetypePrompts] || '';
}

/**
 * Get metadata for an archetype (label, emoji, colors, etc.)
 */
export function getArchetypeMetadata(archetype: CharacterArchetype) {
  return CHARACTER_ARCHETYPES[archetype];
}

/**
 * Get the style preset that corresponds to an archetype
 * This ensures style_preset is always derived from archetype
 */
export function getStylePresetForArchetype(archetype: CharacterArchetype): CharacterStylePreset {
  return CHARACTER_ARCHETYPES[archetype].stylePreset;
}
